/**
 * Civic Scraper — editable registry grid within Data Sources.
 *
 * Columns per row: name · state · level · feed URL (editable) · health badge
 * · page signal · Location/Jurisdiction (inline typeahead) · match status
 * (editable) · notes (editable) · provision/source · job state · actions.
 *
 *   Save   — Enter in the row or the Save button → PATCH the registry row.
 *   Deploy — separate button → upserts the sources feed + enqueues the
 *            scrape_source job (Option B locked semantics).
 *   Recheck — re-runs the page probe for the row.
 *
 * Filters (server-side): resolution / match status / health / provision /
 * source link / whitelisted / search.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/apiFetch';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  RefreshCw,
  Download,
  Rocket,
  Save,
  Loader2,
  ExternalLink,
  Globe,
  TriangleAlert,
  Plus,
} from 'lucide-react';
import { JurisdictionPicker } from './JurisdictionPicker';
import {
  SourceDialog,
  buildCrawlConfig,
} from '@/components/sources/CreateSourceDialog';

interface JobState {
  status: string;
  created_at: string;
}

const STATE_CODES: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

/** humanize "coryell_county" → "Coryell County". */
function humanizeSeg(seg: string): string {
  return seg
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Full jurisdiction display name: "<City/Place> - <County>, <ST>".
 * Built from the jurisdiction path (state.county.place) + jurisdiction name.
 */
function jurisdictionFullName(
  jurisdiction: { name?: string; path?: string } | null | undefined,
  fallbackName: string,
  stateCode: string | null
): string {
  const path = jurisdiction?.path ?? '';
  const segs = path.split('.').filter(Boolean);
  if (segs.length >= 3) {
    const state = STATE_CODES[segs[0]!] ?? humanizeSeg(segs[0]!);
    const county = humanizeSeg(segs[1]!);
    const place = jurisdiction?.name ?? humanizeSeg(segs[segs.length - 1]!);
    return `${place} - ${county}, ${state}`;
  }
  if (segs.length === 2) {
    const state = STATE_CODES[segs[0]!] ?? humanizeSeg(segs[0]!);
    return `${jurisdiction?.name ?? humanizeSeg(segs[1]!)}, ${state}`;
  }
  return `${jurisdiction?.name ?? fallbackName}${
    stateCode ? ` (${stateCode})` : ''
  }`;
}

interface SiteRow {
  id: string;
  name: string;
  state: string | null;
  country: string | null;
  govt_level: string | null;
  end_point: string;
  url_final: string | null;
  url_status: number | null;
  url_error: string | null;
  page_place: string | null;
  whitelisted: boolean;
  jurisdiction_id: string | null;
  jurisdiction?: { name?: string; path?: string } | null;
  match_status: string;
  match_notes: string | null;
  provision_status: string;
  sources_source_id: string | null;
  job_state?: JobState | null;
  /** Local (unsaved) picked jurisdiction path — shown immediately on select. */
  jurisdiction_path?: string | null;
}

interface ListResponse {
  data: SiteRow[];
  total: number;
  page: number;
  pageSize: number;
}

const MATCH_COLORS: Record<string, string> = {
  matched: 'bg-green-100 text-green-800',
  confirmed: 'bg-blue-100 text-blue-800',
  variant: 'bg-amber-100 text-amber-800',
  gap: 'bg-amber-100 text-amber-800',
  county: 'bg-orange-100 text-orange-800',
  entity: 'bg-slate-100 text-slate-700',
  no_match: 'bg-red-100 text-red-700',
  ambiguity: 'bg-purple-100 text-purple-700',
  not_reviewed: 'bg-gray-100 text-gray-600',
};

const JOB_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  running: 'bg-blue-100 text-blue-700',
  succeeded: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

function healthBadge(s: SiteRow) {
  if (s.url_status === 200)
    return <Badge className="bg-green-100 text-green-700">200</Badge>;
  if (s.url_status === 403 || s.url_status === 404)
    return (
      <Badge className="bg-amber-100 text-amber-700">{s.url_status}</Badge>
    );
  return <Badge className="bg-red-100 text-red-700">dead</Badge>;
}

const FILTER_DEFAULTS = {
  resolution: 'all',
  match_status: 'all',
  health: 'all',
  provision: 'all',
  source: 'all',
  whitelisted: 'false',
  search: '',
};

export function CivicScraperPage() {
  usePageTitle('Civic Scraper — Data Sources');
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState<SiteRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Per-row edit state: dirty fields keyed by row id.
  const [dirty, setDirty] = useState<Record<string, Partial<SiteRow>>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deployingId, setDeployingId] = useState<string | null>(null);
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  /** Row opened in the shared "create data source" dialog (prefilled). */
  const [openDialogId, setOpenDialogId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== 'all' && !(k === 'whitelisted' && v === 'false'))
          params.set(k, v);
      });
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const resp = await apiFetch(
        `/api/civic-scraper/sites?${params.toString()}`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as ListResponse;
      setRows(json.data);
      setTotal(json.total);
    } catch (err) {
      toast.error('Failed to load civic scrape registry', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const patchRow = useCallback(
    (id: string, patch: Partial<SiteRow>) =>
      setDirty((d) => ({ ...d, [id]: { ...(d[id] ?? {}), ...patch } })),
    []
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  async function saveRow(id: string) {
    const d = dirty[id];
    if (!d) return;
    setSavingId(id);
    try {
      const body: Record<string, unknown> = {};
      if (d.jurisdiction_id !== undefined)
        body.jurisdiction_id = d.jurisdiction_id;
      if (d.match_status !== undefined) body.match_status = d.match_status;
      if (d.match_notes !== undefined) body.match_notes = d.match_notes;
      if (d.end_point !== undefined) body.end_point = d.end_point;
      const resp = await apiFetch(`/api/civic-scraper/sites/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const j = (await resp.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(j?.error ?? `HTTP ${resp.status}`);
      }
      setDirty((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success('Row saved');
      void fetchRows();
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSavingId(null);
    }
  }

  async function deployRow(row: SiteRow) {
    if (!row.jurisdiction_id) {
      toast.error('Deploy blocked', {
        description: 'Assign a jurisdiction first (Save the row), then Deploy.',
      });
      return;
    }
    if (row.url_status !== 200) {
      const ok = window.confirm(
        `Feed health is "${row.url_status === null ? 'unknown' : row.url_status}" — deploy anyway?`
      );
      if (!ok) return;
    }
    setDeployingId(row.id);
    try {
      const resp = await apiFetch(`/api/civic-scraper/sites/${row.id}/deploy`, {
        method: 'POST',
      });
      if (!resp.ok) {
        const j = (await resp.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(j?.error ?? `HTTP ${resp.status}`);
      }
      const j = (await resp.json()) as {
        data: { source_id: string; job_id: string | null };
      };
      toast.success('Deployed to queue', {
        description: `source ${j.data.source_id.slice(0, 8)}… · job ${(j.data.job_id ?? '').slice(0, 8)}…`,
      });
      void fetchRows();
    } catch (err) {
      toast.error('Deploy failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setDeployingId(null);
    }
  }

  async function recheckRow(id: string) {
    setRecheckingId(id);
    try {
      const resp = await apiFetch(`/api/civic-scraper/sites/${id}/recheck`, {
        method: 'POST',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success('Probe re-run');
      void fetchRows();
    } catch (err) {
      toast.error('Recheck failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRecheckingId(null);
    }
  }

  function exportCsv() {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== 'all' && !(k === 'whitelisted' && v === 'false'))
        params.set(k, v);
    });
    window.location.href = `/api/civic-scraper/sites/export?${params.toString()}`;
  }

  const filteredCount = useMemo(() => total, [total]);

  const prefillRow = rows.find((r) => r.id === openDialogId) ?? null;
  const prefillInitial = prefillRow
    ? {
        id: `prefill-${prefillRow.id}`,
        name: jurisdictionFullName(
          prefillRow.jurisdiction,
          prefillRow.name,
          prefillRow.state
        ),
        base_url: prefillRow.url_final ?? prefillRow.end_point,
        source_type: 'agenda_portal',
        schedule_cron: null,
        enabled: true,
        last_run_at: null,
        last_status: null,
        crawl_config: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        jurisdiction_id: prefillRow.jurisdiction_id,
        jurisdiction_fips: null,
      }
    : undefined;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Civic Scraper</h1>
          <p className="text-sm text-gray-500">
            CivicPlus registry — {filteredCount.toLocaleString()} sites · save
            edits per row · Deploy sends the feed into the scrape queue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SourceDialog
            title="Add source"
            description="Register a new county or government data source for ingestion."
            onSave={async (data) => {
              if (!data.jurisdiction_id) {
                throw new Error(
                  'A jurisdiction is required — select one from the jurisdiction field before saving.'
                );
              }
              const res = await apiFetch('/api/sources', {
                method: 'POST',
                body: JSON.stringify({
                  name: data.name,
                  base_url: data.url,
                  source_type: data.source_type,
                  schedule_cron: null,
                  crawl_config: buildCrawlConfig(data.crawl),
                  jurisdiction_id: data.jurisdiction_id || null,
                  jurisdiction_fips: data.jurisdiction_fips || null,
                }),
              });
              if (!res.ok) {
                const j = (await res.json().catch(() => null)) as {
                  error?: string;
                  message?: string;
                } | null;
                throw new Error(j?.message ?? j?.error ?? `HTTP ${res.status}`);
              }
              toast.success('Source created');
              void fetchRows();
            }}
            trigger={
              <Button
                size="sm"
                className="h-8 bg-violet-600 hover:bg-violet-700 text-white"
              >
                <Plus className="w-3 h-3 mr-1" /> Create New DataSource
              </Button>
            }
          />
          <Button variant="outline" size="sm" onClick={() => void fetchRows()}>
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="w-3 h-3 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[140px]">
              <Label className="text-[11px] text-gray-500">Resolution</Label>
              <Select
                value={filters.resolution}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, resolution: v }))
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="unresolved">Unresolved</SelectItem>
                  <SelectItem value="ambiguity">Ambiguity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-[11px] text-gray-500">Match status</Label>
              <Select
                value={filters.match_status}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, match_status: v }))
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="matched">Matched</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="variant">Variant</SelectItem>
                  <SelectItem value="gap">Gap</SelectItem>
                  <SelectItem value="county">County</SelectItem>
                  <SelectItem value="entity">Entity</SelectItem>
                  <SelectItem value="no_match">No match</SelectItem>
                  <SelectItem value="ambiguity">Ambiguity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-[11px] text-gray-500">Health</Label>
              <Select
                value={filters.health}
                onValueChange={(v) => setFilters((f) => ({ ...f, health: v }))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="healthy">Healthy (200)</SelectItem>
                  <SelectItem value="dead">Dead</SelectItem>
                  <SelectItem value="blocked">Blocked (403/404)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-[11px] text-gray-500">Provision</Label>
              <Select
                value={filters.provision}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, provision: v }))
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="provisioned">Provisioned</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-[11px] text-gray-500">Source link</Label>
              <Select
                value={filters.source}
                onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="has">Has feed</SelectItem>
                  <SelectItem value="none">No feed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-1.5 pb-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.whitelisted === 'true'}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    whitelisted: e.target.checked ? 'true' : 'false',
                  }))
                }
                className="rounded"
              />
              Whitelisted only
            </label>
            <div className="relative min-w-[240px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                className="h-8 pl-8 text-xs"
                placeholder="Search name / state / URL / signal…"
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Grid ── */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Name</TableHead>
                <TableHead className="w-12">ST</TableHead>
                <TableHead className="w-24">Level</TableHead>
                <TableHead className="min-w-[220px]">Feed URL</TableHead>
                <TableHead className="w-16">Health</TableHead>
                <TableHead className="w-36">Page signal</TableHead>
                <TableHead className="min-w-[220px]">
                  Location / Jurisdiction
                </TableHead>
                <TableHead className="w-24">Match</TableHead>
                <TableHead className="min-w-[140px]">Notes</TableHead>
                <TableHead className="w-32">Feed / Job</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="text-center py-8 text-gray-400 text-sm"
                  >
                    <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                    Loading registry…
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const d = dirty[row.id];
                  const hasDirty = !!d && Object.keys(d).length > 0;
                  return (
                    <TableRow
                      key={row.id}
                      className={hasDirty ? 'bg-amber-50/50' : ''}
                    >
                      <TableCell>
                        <div className="text-sm font-medium text-gray-900">
                          {row.name}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {row.country ?? ''}{' '}
                          {!row.whitelisted && (
                            <span className="text-red-400">
                              · not whitelisted
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {row.state ?? '—'}
                      </TableCell>
                      <TableCell className="text-[11px] text-gray-500">
                        {row.govt_level ?? '—'}
                      </TableCell>
                      <TableCell>
                        <input
                          className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px] text-gray-700 focus:border-blue-400 focus:outline-none"
                          value={d?.end_point ?? row.end_point}
                          onChange={(e) =>
                            patchRow(row.id, { end_point: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void saveRow(row.id);
                            }
                          }}
                          title="Feed URL — Save with Enter"
                        />
                      </TableCell>
                      <TableCell>{healthBadge(row)}</TableCell>
                      <TableCell>
                        <div
                          className="text-[11px] text-gray-700 truncate"
                          title={row.page_place ?? ''}
                        >
                          {row.page_place ?? (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 min-w-[200px]">
                          <JurisdictionPicker
                            value={row.jurisdiction_id}
                            displayName={row.jurisdiction?.name ?? null}
                            onSelect={(jid, name, path) => {
                              patchRow(row.id, {
                                jurisdiction_id: jid,
                                match_status: 'confirmed',
                                jurisdiction_path: path ?? undefined,
                              });
                              toast.info(
                                `Jurisdiction set — save to persist${row.jurisdiction?.name === name ? '' : ''}`
                              );
                            }}
                            onClear={() =>
                              patchRow(row.id, {
                                jurisdiction_id: null,
                                jurisdiction_path: undefined,
                              })
                            }
                          />
                          {(d?.jurisdiction_path ?? row.jurisdiction?.path) ? (
                            <span
                              className="block font-mono text-[10px] font-medium text-gray-900 leading-relaxed break-all"
                              title={
                                d?.jurisdiction_path ?? row.jurisdiction?.path
                              }
                            >
                              {d?.jurisdiction_path ?? row.jurisdiction?.path}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <select
                          value={d?.match_status ?? row.match_status}
                          onChange={(e) =>
                            patchRow(row.id, { match_status: e.target.value })
                          }
                          className="rounded border border-gray-200 text-[11px] px-1 py-1 text-gray-700"
                        >
                          {Object.keys(MATCH_COLORS).map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        <Badge
                          className={`mt-1 ${MATCH_COLORS[row.match_status] ?? ''}`}
                        >
                          {row.match_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <textarea
                          rows={2}
                          className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px] text-gray-700 focus:border-blue-400 focus:outline-none"
                          value={d?.match_notes ?? row.match_notes ?? ''}
                          onChange={(e) =>
                            patchRow(row.id, { match_notes: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              void saveRow(row.id);
                            }
                          }}
                          placeholder="review notes…"
                        />
                      </TableCell>
                      <TableCell>
                        {row.sources_source_id ? (
                          <div className="space-y-1">
                            <Link
                              to="/sources"
                              className="flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                              title={row.sources_source_id}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Provisioned
                            </Link>
                            <div>
                              {row.job_state ? (
                                <Badge
                                  className={
                                    JOB_COLORS[row.job_state.status] ?? ''
                                  }
                                >
                                  {row.job_state.status}
                                </Badge>
                              ) : (
                                <span className="text-[10px] text-gray-400">
                                  no job yet
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1.5">
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px]"
                              disabled={!hasDirty || savingId === row.id}
                              onClick={() => void saveRow(row.id)}
                            >
                              {savingId === row.id ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Save className="w-3 h-3 mr-1" />
                              )}
                              Save
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700"
                              disabled={deployingId === row.id}
                              onClick={() => void deployRow(row)}
                            >
                              {deployingId === row.id ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Rocket className="w-3 h-3 mr-1" />
                              )}
                              Deploy
                            </Button>
                            <button
                              type="button"
                              aria-label="Create new data source"
                              title={`Create data source for ${row.jurisdiction?.name ?? row.name}${row.state ? ` (${row.state})` : ''}`}
                              className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-600 text-white hover:bg-violet-700"
                              onClick={() => {
                                if (!row.jurisdiction_id) {
                                  toast.error('No jurisdiction assigned', {
                                    description:
                                      'Assign a jurisdiction in this row first, then create the data source.',
                                  });
                                  return;
                                }
                                setOpenDialogId(row.id);
                              }}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-[10px] text-gray-500 hover:text-blue-600 flex items-center gap-0.5"
                              disabled={recheckingId === row.id}
                              onClick={() => void recheckRow(row.id)}
                            >
                              {recheckingId === row.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Globe className="w-3 h-3" />
                              )}
                              Recheck
                            </button>
                            {row.url_status !== 200 && hasDirty ? null : null}
                            {row.url_status !== 200 ? (
                              <TriangleAlert className="w-3 h-3 text-amber-500" />
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Pagination ── */}
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>
          {total.toLocaleString()} rows · page {page} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(parseInt(v, 10));
              setPage(1);
            }}
          >
            <SelectTrigger className="h-7 text-xs w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100].map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>

      {/* ── Shared create-source dialog — prefilled from the row's [+] ── */}
      <SourceDialog
        title="Create new Data Source"
        description="Jurisdiction and name are pre-filled from this feed row — adjust and save."
        open={openDialogId !== null}
        onOpenChange={(o) => {
          if (!o) setOpenDialogId(null);
        }}
        initial={prefillInitial}
        onSave={async (data) => {
          if (!data.jurisdiction_id) {
            throw new Error(
              'A jurisdiction is required — select one from the jurisdiction field before saving.'
            );
          }
          const res = await apiFetch('/api/sources', {
            method: 'POST',
            body: JSON.stringify({
              name: data.name,
              base_url: data.url,
              source_type: data.source_type,
              schedule_cron: null,
              crawl_config: buildCrawlConfig(data.crawl),
              jurisdiction_id: data.jurisdiction_id || null,
              jurisdiction_fips: data.jurisdiction_fips || null,
            }),
          });
          if (!res.ok) {
            const j = (await res.json().catch(() => null)) as {
              error?: string;
              message?: string;
            } | null;
            throw new Error(j?.message ?? j?.error ?? `HTTP ${res.status}`);
          }
          setOpenDialogId(null);
          toast.success('Source created');
          void fetchRows();
        }}
        trigger={<span />}
      />
    </div>
  );
}
